// Client-side OCR via tesseract.js. tesseract references Web Workers / WASM, so it is
// dynamically imported (never on the server) and the worker is created lazily + reused
// across a batch, then terminated. Used to auto-generate alt text for image slices.

/**
 * Tidy raw OCR output into something usable as an alt attribute.
 *
 * Beyond collapsing whitespace, drop junk tokens: pure punctuation/symbols and
 * symbol-heavy fragments (e.g. "(g@S«") that OCR hallucinates from graphics/icons in a
 * marketing slice. This measurably cleans the alt text — verified against real slices,
 * grayscale+normalize preprocessing + PSM 3 gives the best raw text and this removes the
 * remaining noise. Reading order in multi-column art can still interleave; that's inherent
 * and acceptable for alt text.
 *
 * No length cap — the full recognized text is returned; alt text is editable afterwards.
 */
export function cleanOcrText(raw: string): string {
  const collapsed = (raw || '')
    .replace(/[^\S ]+/g, ' ') // newlines/tabs → space
    .replace(/\s+/g, ' ')
    .trim()

  const kept = collapsed.split(' ').filter((tok) => {
    if (!tok) return false
    const alnum = (tok.match(/[a-z0-9]/gi) || []).length
    if (alnum === 0) return false // pure punctuation/symbols ("«", "@", "<Q)")
    // Fragments dominated by non-word symbols are almost always graphic noise.
    const sym = (tok.match(/[^a-z0-9%$.,'’"()\-/]/gi) || []).length
    if (tok.length >= 3 && sym / tok.length > 0.4) return false
    return true
  })

  return kept.join(' ')
}

/**
 * Grayscale + contrast-stretch a slice before OCR. Colored marketing copy on colored
 * backgrounds trips up Tesseract; normalizing luminance to the full 0–255 range recovers
 * text the raw color image loses (e.g. the big "44%"). Mirrors sharp's grayscale().normalize()
 * using Canvas 2D (sharp is server-only). Returns a new canvas; input is left untouched.
 */
function preprocess(source: HTMLCanvasElement): HTMLCanvasElement {
  const w = source.width
  const h = source.height
  const out = document.createElement('canvas')
  out.width = w
  out.height = h
  const src = source.getContext('2d')
  const dst = out.getContext('2d')
  if (!src || !dst) return source

  const img = src.getImageData(0, 0, w, h)
  const d = img.data
  const n = d.length / 4

  // Pass 1: luminance + histogram.
  const lum = new Uint8ClampedArray(n)
  const hist = new Uint32Array(256)
  for (let i = 0, p = 0; i < d.length; i += 4, p++) {
    const g = (d[i]! * 0.299 + d[i + 1]! * 0.587 + d[i + 2]! * 0.114) | 0
    lum[p] = g
    hist[g]!++
  }

  // Percentile clip points (like sharp's normalize) — robust to stray pure black/white
  // pixels that a naive min/max stretch would collapse on.
  const clip = Math.floor(n * 0.01)
  let lo = 0
  let hi = 255
  for (let c = 0, s = 0; c < 256; c++) { s += hist[c]!; if (s > clip) { lo = c; break } }
  for (let c = 255, s = 0; c >= 0; c--) { s += hist[c]!; if (s > clip) { hi = c; break } }
  const range = hi - lo || 1

  // Pass 2: rescale the clipped range to full 0–255.
  for (let i = 0, p = 0; i < d.length; i += 4, p++) {
    let v = ((lum[p]! - lo) * 255 / range) | 0
    v = v < 0 ? 0 : v > 255 ? 255 : v
    d[i] = d[i + 1] = d[i + 2] = v
  }
  dst.putImageData(img, 0, 0)
  return out
}

export function useOcr() {
  let workerPromise: Promise<any> | null = null

  async function getWorker(onProgress?: (p: number) => void) {
    if (!workerPromise) {
      workerPromise = (async () => {
        const { createWorker } = await import('tesseract.js')
        const worker = await createWorker('eng', 1, {
          logger: onProgress
            ? (m: any) => {
                if (m.status === 'recognizing text' && typeof m.progress === 'number') onProgress(m.progress)
              }
            : undefined,
        })
        // PSM 3 (auto page segmentation) beat single-block modes on real slices; keep word
        // spacing so the alt text reads naturally.
        await worker.setParameters({ tessedit_pageseg_mode: '3', preserve_interword_spaces: '1' })
        return worker
      })()
    }
    return workerPromise
  }

  /** Recognize text in an image (canvas, blob, or data URL). Returns cleaned alt-ready text. */
  async function recognize(
    image: HTMLCanvasElement | Blob | string,
    onProgress?: (p: number) => void,
  ): Promise<string> {
    try {
      const worker = await getWorker(onProgress)
      // Preprocess canvases (what the slicer passes); other inputs go through as-is.
      const input = typeof HTMLCanvasElement !== 'undefined' && image instanceof HTMLCanvasElement
        ? preprocess(image)
        : image
      const { data } = await worker.recognize(input)
      return cleanOcrText(data?.text || '')
    } catch (err) {
      console.error('OCR failed:', err)
      return ''
    }
  }

  async function terminate() {
    if (workerPromise) {
      try {
        const w = await workerPromise
        await w.terminate()
      } catch {
        // already gone
      }
      workerPromise = null
    }
  }

  return { recognize, terminate }
}
